import { IHowItWorksTab, IWhatToDropItem } from "../../store/useHowItWorks";

function toEmbedUrl(url: string): string | null {
  const short = url.match(/youtu\.be\/([^?&]+)/);
  if (short) return `https://www.youtube.com/embed/${short[1]}`;
  const long = url.match(/[?&]v=([^?&]+)/);
  if (long) return `https://www.youtube.com/embed/${long[1]}`;
  if (url.includes("youtube.com/embed/")) return url;
  return null;
}

const SECTION_TITLES: Record<string, { en: string; nl: string }> = {
  "how-it-works": { en: "HOW IT WORKS", nl: "HOE HET WERKT" },
  "what-to-drop": { en: "PREPARE DROPS", nl: "DROPS VOORBEREIDEN" },
  "learn-more":   { en: "LEARN MORE",   nl: "MEER INFORMATIE" },
};

const ITEM_LABELS = {
  step:         { en: "Step",                  nl: "Stap" },
  dos:          { en: "DOs",                   nl: "Welles" },
  donts:        { en: "DON'Ts",                nl: "Nietes" },
  how:          { en: "How",                   nl: "Hoe?" },
  what_happens: { en: "What happens to it?",   nl: "Wat gebeurt ermee?" },
  collapse:     { en: "Collapse this section", nl: "Sectie sluiten" },
};

// Tab row is fixed — images and labels are not editable from the dashboard.
const STATIC_TABS = [
  { type: "how-it-works", en: "How it works", nl: "Hoe het werkt", img: "/onboarding1.png" },
  { type: "what-to-drop", en: "What to drop", nl: "Wat kun je droppen", img: "/onboarding2.png" },
  { type: "learn-more",   en: "Learn more",   nl: "Meer informatie", img: "/onboarding3.png" },
] as const;

interface Props {
  tabs: IHowItWorksTab[];
  items: IWhatToDropItem[];
  activeTabType: "how-it-works" | "what-to-drop" | "learn-more";
  activeItemKey: string | null;
  onSelectItem: (key: string | null) => void;
  lang: "en" | "nl";
}

function t<T>(obj: { en: T; nl: T }, lang: "en" | "nl"): T {
  return lang === "nl" ? obj.nl : obj.en;
}

export default function HowItWorksPreview({
  tabs,
  items,
  activeTabType,
  activeItemKey,
  onSelectItem,
  lang,
}: Props) {
  const activeTab = tabs.find((t) => t.type === activeTabType);
  // All items laid out 3 per row (matches the app — no fixed cap, no dimmed row).
  const itemRows: (typeof items)[] = [];
  for (let i = 0; i < items.length; i += 3) {
    itemRows.push(items.slice(i, i + 3));
  }
  const activeItem = items.find((i) => i.key === activeItemKey) ?? null;
  const panelTextColor = activeItem?.panelTextColor || "#FFFFFF";

  return (
    /* Phone frame */
    <div
      className="w-72 bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mx-auto"
      style={{ minHeight: 520 }}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1 border-b border-gray-100">
        <span className="text-xs font-bold text-gray-500">How it works</span>
        <span className="text-gray-300 text-xs">✕</span>
      </div>

      {/* Tab row — static, not editable from dashboard */}
      <div className="flex gap-2 px-3 pt-3 pb-2">
        {STATIC_TABS.map((tab) => (
          <div
            key={tab.type}
            className="flex-1 flex flex-col items-center gap-1 cursor-default"
          >
            <div
              className={`w-full aspect-square rounded-lg border overflow-hidden ${
                tab.type === activeTabType ? "border-blue-200 shadow-sm" : "border-gray-200"
              }`}
            >
              <img
                src={tab.img}
                alt={tab.en}
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
            <span className="text-center leading-tight" style={{ fontSize: 9, color: "#1F1F1F", fontWeight: 600 }}>
              {lang === "nl" ? tab.nl : tab.en}
            </span>
          </div>
        ))}
      </div>

      {/* Section content */}
      <div className="px-3 pb-4 overflow-y-auto" style={{ maxHeight: 420 }}>
        {activeTab && (
          <>
            {/* Header block */}
            <div
              className="inline-block rounded-2xl px-4 py-3 mb-2 mt-1"
              style={{ backgroundColor: activeTab.sectionColor }}
            >
              <div
                className="font-bold"
                style={{ fontSize: 22, lineHeight: "1.1", color: "#111", fontFamily: "serif" }}
              >
                {SECTION_TITLES[activeTabType]?.[lang] ?? SECTION_TITLES[activeTabType]?.en}
              </div>
            </div>

            {/* Learn more top title — above the description */}
            {activeTabType === "learn-more" &&
              t({ en: activeTab.en_topTitle ?? "", nl: activeTab.nl_topTitle ?? "" }, lang) && (
                <p style={{ fontSize: 14, fontWeight: 700, color: "#111", marginBottom: 4 }}>
                  {t({ en: activeTab.en_topTitle ?? "", nl: activeTab.nl_topTitle ?? "" }, lang)}
                </p>
              )}

            {/* Description */}
            {t({ en: activeTab.en_description, nl: activeTab.nl_description }, lang) && (
              <p className="text-gray-500 mb-3" style={{ fontSize: 10, lineHeight: "1.4" }}>
                {t({ en: activeTab.en_description, nl: activeTab.nl_description }, lang)}
              </p>
            )}

            {/* How it works: steps */}
            {activeTabType === "how-it-works" && (
              <div className="space-y-2">
                {t({ en: activeTab.en_steps, nl: activeTab.nl_steps }, lang).map((step, i) => (
                  <div key={i}>
                    <p className="font-bold text-orange-500 mb-1" style={{ fontSize: 10 }}>{t(ITEM_LABELS.step, lang)} {i + 1}</p>
                    <div
                      className="rounded-xl px-3 py-2"
                      style={{ backgroundColor: activeTab.sectionColor }}
                    >
                      <p style={{ fontSize: 10, color: "#292929" }}>{step}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* What to drop: material items */}
            {activeTabType === "what-to-drop" && (
              <>
                {/* Legend bullets */}
                {t({ en: activeTab.en_details, nl: activeTab.nl_details }, lang).length > 0 && (
                  <div className="mb-2">
                    {t({ en: activeTab.en_details, nl: activeTab.nl_details }, lang).map((d, i) => (
                      <p key={i} style={{ fontSize: 9, color: "#5A5A5A", lineHeight: "1.5" }}>• {d}</p>
                    ))}
                  </div>
                )}

                {/* Material item rows — all items, 3 per row, clickable */}
                {itemRows.map((rowItems, rowIndex) => {
                  const activeInThisRow =
                    !!activeItem &&
                    rowItems.some((it) => it.key === activeItem.key);
                  return (
                    <div key={`row-${rowIndex}`}>
                      <div className={`flex gap-1 mb-1 ${rowIndex > 0 ? "mt-1" : ""}`}>
                        {rowItems.map((item) => {
                    const isActive = activeItemKey === item.key;
                    return (
                      <button
                        key={item.key}
                        onClick={() => onSelectItem(isActive ? null : item.key)}
                        className="flex-1 rounded-2xl flex items-center justify-center relative"
                        style={{
                          aspectRatio: "1.4",
                          backgroundColor: isActive ? item.containerColor : "#D7E3F7",
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#2F2F2F",
                          padding: "4px",
                          textAlign: "center",
                          lineHeight: "1.2",
                        }}
                      >
                        {t({ en: item.en_title, nl: item.nl_title }, lang)}
                        {/* connector */}
                        {isActive && (
                          <div
                            className="absolute bottom-0 left-0 right-0 h-3"
                            style={{ backgroundColor: item.containerColor, zIndex: 1 }}
                          />
                        )}
                      </button>
                    );
                  })}
                        {/* Invisible fillers keep a partial row left-aligned */}
                        {Array.from({ length: Math.max(0, 3 - rowItems.length) }).map((_, i) => (
                          <div key={`ph-${rowIndex}-${i}`} className="flex-1" style={{ aspectRatio: "1.4" }} />
                        ))}
                      </div>

                      {/* Active item detail panel — below the active item's row */}
                      {activeInThisRow && activeItem && (
                        <div
                          className="rounded-2xl p-3 mb-2"
                          style={{
                            backgroundColor: activeItem.containerColor,
                            borderTopLeftRadius: activeItem.key === rowItems[0]?.key ? 0 : undefined,
                            borderTopRightRadius: activeItem.key === rowItems[2]?.key ? 0 : undefined,
                          }}
                        >
                    {/* Icons card */}
                    <div
                      className="rounded-xl flex items-center justify-between px-2 py-1 mb-2"
                      style={{ backgroundColor: activeItem.itemsImageBgColor }}
                    >
                      {activeItem.itemsImage ? (
                        <img src={activeItem.itemsImage} alt="" className="h-4 object-contain" />
                      ) : (
                        <span style={{ fontSize: 9, color: "#aaa" }}>Icons image</span>
                      )}
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#232323", fontFamily: "serif" }}>
                        {t({ en: activeItem.en_price, nl: activeItem.nl_price }, lang)}
                      </span>
                    </div>

                    {/* DOs */}
                    {t({ en: activeItem.en_dos, nl: activeItem.nl_dos }, lang).length > 0 && (
                      <>
                        <p className="font-bold mb-1" style={{ fontSize: 10, color: activeItem.accentColor }}>{t(ITEM_LABELS.dos, lang)}</p>
                        <div
                          className="rounded-xl px-3 py-2 mb-2"
                          style={{ backgroundColor: activeItem.panelColor, marginLeft: -6, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        >
                          {t({ en: activeItem.en_dos, nl: activeItem.nl_dos }, lang).map((d, i) => (
                            <p key={i} style={{ fontSize: 9, color: panelTextColor }}>• {d}</p>
                          ))}
                        </div>
                      </>
                    )}

                    {/* DON'Ts */}
                    {t({ en: activeItem.en_donts, nl: activeItem.nl_donts }, lang).length > 0 && (
                      <>
                        <p className="font-bold mb-1" style={{ fontSize: 10, color: activeItem.accentColor }}>{t(ITEM_LABELS.donts, lang)}</p>
                        <div
                          className="rounded-xl px-3 py-2 mb-2"
                          style={{ backgroundColor: activeItem.panelColor, marginRight: -6, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                        >
                          {t({ en: activeItem.en_donts, nl: activeItem.nl_donts }, lang).map((d, i) => (
                            <p key={i} style={{ fontSize: 9, color: panelTextColor }}>• {d}</p>
                          ))}
                        </div>
                      </>
                    )}

                    {/* How */}
                    {t({ en: activeItem.en_howText, nl: activeItem.nl_howText }, lang).length > 0 && (
                      <>
                        <p className="font-bold mb-1" style={{ fontSize: 10, color: activeItem.accentColor }}>{t(ITEM_LABELS.how, lang)}</p>
                        <div className="bg-white rounded-xl px-3 py-2 mb-2 relative overflow-hidden" style={{ minHeight: 48 }}>
                          <div style={{ maxWidth: "65%" }}>
                            {t({ en: activeItem.en_howText, nl: activeItem.nl_howText }, lang).map((d, i) => (
                              <p key={i} style={{ fontSize: 9, color: "#222", fontWeight: 600 }}>• {d}</p>
                            ))}
                          </div>
                          {activeItem.methodImage && (
                            <img
                              src={activeItem.methodImage}
                              alt=""
                              className="absolute right-1 top-0 bottom-0 object-contain"
                              style={{ height: "120%", maxWidth: 48, top: -4 }}
                            />
                          )}
                        </div>
                      </>
                    )}

                    {/* What happens — text left, optional partner logo right */}
                    {t({ en: activeItem.en_whatHappensText, nl: activeItem.nl_whatHappensText }, lang) && (
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <p className="font-bold mb-1" style={{ fontSize: 10, color: activeItem.accentColor }}>{t(ITEM_LABELS.what_happens, lang)}</p>
                          <p style={{ fontSize: 9, color: "#2E2E2E", lineHeight: "1.5" }}>
                            {t({ en: activeItem.en_whatHappensText, nl: activeItem.nl_whatHappensText }, lang)}
                          </p>
                        </div>
                        {activeItem.whatHappensImage && (
                          <img
                            src={activeItem.whatHappensImage}
                            alt=""
                            className="object-contain"
                            style={{ width: "30%", alignSelf: "center" }}
                          />
                        )}
                      </div>
                    )}

                    {/* Video */}
                    {activeItem.videoUrl && toEmbedUrl(activeItem.videoUrl) && (
                      <div className="mt-2 rounded-xl overflow-hidden" style={{ aspectRatio: "16/9" }}>
                        <iframe
                          src={toEmbedUrl(activeItem.videoUrl)!}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title="video"
                        />
                      </div>
                    )}

                    {/* Collapse */}
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={() => onSelectItem(null)}
                        style={{ fontSize: 9, color: activeItem.accentColor, fontWeight: 700 }}
                      >
                        {t(ITEM_LABELS.collapse, lang)}
                      </button>
                    </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {/* Learn more: titled bullet sections + videos */}
            {activeTabType === "learn-more" && (
              <div className="space-y-2">
                {(activeTab.learnMoreSections ?? []).map((sec, si) => {
                  const secTitle = t({ en: sec.en_title, nl: sec.nl_title }, lang);
                  const secBullets = t({ en: sec.en_bullets, nl: sec.nl_bullets }, lang);
                  return (
                    <div key={si} className="mb-1">
                      {secTitle && (
                        <p style={{ fontSize: 12, fontWeight: 700, color: "#1F1F1F", marginBottom: 2 }}>{secTitle}</p>
                      )}
                      {secBullets.map((d, i) => (
                        <p key={i} style={{ fontSize: 11, color: "#3F3F3F", lineHeight: "1.5" }}>• {d}</p>
                      ))}
                    </div>
                  );
                })}
                {(activeTab.videos ?? []).map((url, i) => {
                  const embed = toEmbedUrl(url);
                  if (!embed) return null;
                  return (
                    <div key={i} className="rounded-xl overflow-hidden mt-2" style={{ aspectRatio: "16/9" }}>
                      <iframe
                        src={embed}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={`video-${i}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {!activeTab && (
          <p className="text-xs text-gray-400 text-center mt-8">Loading…</p>
        )}
      </div>
    </div>
  );
}
